import {
  Box,
  Button,
  Flex,
  Image,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Text,
} from '@chakra-ui/react'
import { ScreenContext, useApp } from '@hb/react'
import { Assessment, colors, getAssessmentName } from '@hb/shared'
import { useContext, useMemo } from 'react'
import { useProfile } from '../../../../contexts/user/ProfileContext'
import { useCreateAssessment } from '../../../../hooks/assessments/useCreateAssessment'
import { useNavigate } from '../../../../hooks/navigation'
import { AssessmentIcon } from '../../../../icons/AssessmentIcon'
import personIcon from '../../../../icons/person_fill.svg'
import { Expandable } from '../../../Expandable'
import { AddAssessmentAlert } from './AddAssessmentAlert'

export const AssessmentSelect = () => {
  const {
    goTo,
    userId,
    selectedAssessment: { populated: selectedAssessment },
    assessments,
  } = useProfile()

  const { isMobile } = useContext(ScreenContext)
  const { addLoading, confirmingNewAssessment, createAssessment, setConfirmingNewAssessment } =
    useCreateAssessment()

  const navigate = useNavigate()
  const { appName } = useApp()
  const patientPath = useMemo(
    () => `${appName === 'app' ? '/admin' : ''}/patients/${userId}`,
    [appName, userId],
  )

  const { archivedAssessments, nonArchivedAssessments } = useMemo(() => {
    const arr = Object.entries(assessments ?? {})
    const archived: { id: string; data: Assessment }[] = []
    const nonArchived: { id: string; data: Assessment }[] = []
    arr.forEach(([id, data]) => {
      if (data.archivedOn) {
        archived.push({ id, data })
      } else {
        nonArchived.push({ id, data })
      }
    })
    return {
      archivedAssessments: archived,
      nonArchivedAssessments: nonArchived,
    }
  }, [assessments])

  return (
    <>
      <Menu gutter={2} isLazy strategy="fixed" placement="bottom-start">
        <MenuButton bg="white" shadow="md" minW="0" isLoading={addLoading} px={2} as={Button}>
          <Flex px={1.5} align="center">
            {selectedAssessment ? <AssessmentIcon /> : null}
            <Text
              lineHeight={1}
              py={1}
              fontSize="sm"
              fontWeight={500}
              fontFamily="Hero-New"
              maxW={isMobile ? '100%' : '175px'}
              isTruncated>
              {selectedAssessment ? getAssessmentName(selectedAssessment) : 'Select a Pregnancy'}
            </Text>
          </Flex>
        </MenuButton>
        <Portal>
          <MenuList display="flex" bg="gray.100" maxW="380px" minW="300px" py={0} maxH="350px">
            <Flex w="100%" flexFlow="column" maxH="100%">
              <Box flex={1} minW={0} overflowY="auto">
                <MenuItem
                  onClick={() => navigate(patientPath)}
                  _hover={{ bg: 'rgb(245,245,245)' }}
                  bg="white"
                  borderBottom="1px solid #cdcdcd"
                  px={3}
                  py={2}>
                  <Image width="24px" src={personIcon} mr={2} />
                  Patient Profile
                </MenuItem>
                {nonArchivedAssessments?.length
                  ? nonArchivedAssessments.map(({ id, data }, i) => (
                      <MenuItem
                        key={`${data.createdOn || i}`}
                        onClick={() =>
                          goTo({ entity: { id, type: 'pregnancy' }, tab: 'pregnancy' })
                        }
                        _hover={{ bg: 'rgb(245,245,245)' }}
                        bg="white"
                        borderBottom="1px solid #cdcdcd"
                        px={3}
                        py={2}>
                        <AssessmentIcon mr={2} />
                        {getAssessmentName(data)}
                      </MenuItem>
                    ))
                  : null}
                {archivedAssessments?.length ? (
                  <Expandable
                    header={() => (
                      <Text px={2} py={1} color="red.600" fontSize="sm" fontWeight={600}>
                        ARCHIVED
                      </Text>
                    )}>
                    {archivedAssessments.map(({ id, data }, i) => (
                      <MenuItem
                        key={`${data.createdOn || i}`}
                        borderTop="1px solid #cdcdcd"
                        onClick={() => goTo({ entity: { id, type: 'pregnancy' }, tab: null })}
                        bg="white"
                        _hover={{ bg: 'rgb(245,245,245)' }}
                        px={3}
                        py={2}>
                        <AssessmentIcon />
                        {getAssessmentName(data)}
                      </MenuItem>
                    ))}
                  </Expandable>
                ) : null}
              </Box>
              <MenuItem
                borderTop="1px solid #cdcdcd"
                bg={colors.green.hex}
                _hover={{ bg: 'green.400' }}
                color="white"
                fontFamily="Comfortaa"
                fontWeight={600}
                textShadow="1px 1px 3px rgba(0,0,0,0.5)"
                fontSize="sm"
                onClick={() => setConfirmingNewAssessment(true)}>
                + NEW PREGNANCY
              </MenuItem>
            </Flex>
          </MenuList>
        </Portal>
      </Menu>
      <AddAssessmentAlert
        onConfirm={createAssessment}
        isOpen={confirmingNewAssessment}
        onCancel={() => setConfirmingNewAssessment(false)}
      />
    </>
  )
}
